/**
 * Prompt Profiles API Schemas
 *
 * Zod validation schemas for prompt profiles API endpoints.
 * Profiles are organization-scoped (not project-scoped).
 *
 * Endpoints:
 * - GET /api/v1/prompt-profiles - List profiles
 * - POST /api/v1/prompt-profiles - Create profile
 * - GET /api/v1/prompt-profiles/:id - Get profile
 * - PATCH /api/v1/prompt-profiles/:id - Update profile
 * - DELETE /api/v1/prompt-profiles/:id - Delete profile
 * - POST /api/v1/prompt-profiles/:id/duplicate - Duplicate profile
 * - POST /api/v1/prompt-profiles/preview - Preview analysis
 */

import { z } from 'zod/v4';
import { analyzedCallCategorySchema } from './records';

// ============================================================================
// Enums
// ============================================================================

/**
 * Profile binding - auto-selection key for the analyze pipeline.
 *
 * A bound profile is picked automatically per record by the LLM-detected call
 * category (categorize-step). Chats are categorized the same way: the channel
 * drives the form of the assessment (rubric wording, applicable criteria), not
 * the choice of profile.
 *
 * IS `analyzedCallCategorySchema` — the categories the categorize-step actually
 * emits — rather than `callCategorySchema.exclude(['missed', 'short'])`, which
 * derived the same set by naming the two derived statuses that existed when it
 * was written. Add a third derived status to `callCategorySchema` and that
 * subtraction silently makes it bindable: no type error, no failing test, and
 * an admin able to bind a profile to a category no analysis will ever carry.
 *
 * At most one profile per binding per org (partial unique index in D1).
 * NULL/absent = unbound profile, selectable via projects.promptProfileId.
 * Mutable via PATCH: null unbinds, a value rebinds (conflict → 409).
 */
export const profileBindingSchema = analyzedCallCategorySchema;

export const PROFILE_BINDINGS = profileBindingSchema.options;

/**
 * Profile category - determines the base prompt template and analysis focus
 *
 * - `sales-call` - Voice sales calls with focus on objection handling, BANT qualification, closing techniques
 * - `support-call` - Customer support calls with focus on issue resolution, customer satisfaction, compliance
 * - `sales-chat` - Text-based sales chats with focus on response speed, product knowledge, conversion
 * - `complaint` - Customer complaints with focus on de-escalation, empathy, resolution tracking
 */
export const profileCategorySchema = z.enum([
  'sales-call',
  'support-call',
  'sales-chat',
  'complaint',
]);

// ============================================================================
// Toggles Schema (analysis features)
// ============================================================================

/**
 * Prompt toggles - controls which analysis features are enabled
 *
 * Organized by A-G framework for comprehensive quality assessment:
 * - A. Summary metrics (qualityScore, saleProbability, attentionFlag)
 * - B. Content analysis (topic, keywords, keyMoments, summary, structuredSummary)
 * - C. Speech analysis (fillerWords, stopWords, operatorErrors)
 * - D. Dialogue technique (greetingIntro, needsQualification, objectionHandling, closingTechniques)
 * - E. Emotions (sentiment, politeness, interruptions, clarity)
 * - F. Compliance (crmCompleteness)
 * - G. Recommendations (strengthsImprovements)
 * - H. Business intelligence (businessIntelligence)
 *
 * Each toggle defaults to false. Enable selectively to optimize LLM token usage and analysis cost.
 */
export const promptTogglesSchema = z.object({
  // A. Summary metrics
  qualityScore: z.boolean().optional().default(false), // Overall quality score (0-100)
  saleProbability: z.boolean().optional().default(false), // Likelihood of sale conversion (0-100)
  attentionFlag: z.boolean().optional().default(false), // Flags calls needing supervisor attention

  // B. Content analysis
  topic: z.boolean().optional().default(false), // Main topic/intent classification
  keywords: z.boolean().optional().default(false), // Key entities (products, competitors, issues)
  keyMoments: z.boolean().optional().default(false), // Critical conversation turning points
  summary: z.boolean().optional().default(false), // Plain text summary
  structuredSummary: z.boolean().optional().default(false), // Structured fields (problem, solution, next steps)

  // C. Speech analysis
  fillerWords: z.boolean().optional().default(false), // "um", "uh", "like" frequency
  stopWords: z.boolean().optional().default(false), // Prohibited words/phrases detection
  operatorErrors: z.boolean().optional().default(false), // Common agent mistakes (wrong info, policy violations)

  // D. Dialogue technique
  greetingIntro: z.boolean().optional().default(false), // Proper opening and rapport building
  needsQualification: z.boolean().optional().default(false), // BANT (Budget, Authority, Need, Timeline) assessment
  objectionHandling: z.boolean().optional().default(false), // How objections were addressed
  closingTechniques: z.boolean().optional().default(false), // Call-to-action and next steps clarity

  // E. Emotions
  sentiment: z.boolean().optional().default(false), // Overall emotional tone (positive/negative/neutral)
  politeness: z.boolean().optional().default(false), // Professional language and courtesy
  interruptions: z.boolean().optional().default(false), // Excessive agent interruptions
  clarity: z.boolean().optional().default(false), // Clear communication and explanations

  // F. Compliance
  crmCompleteness: z.boolean().optional().default(false), // CRM data entry validation

  // G. Recommendations
  strengthsImprovements: z.boolean().optional().default(false), // AI-generated coaching suggestions

  // H. Business intelligence
  businessIntelligence: z.boolean().optional().default(false), // Competitor mentions, product feedback, market signals, compliance flags
});

// ============================================================================
// Path Param Schemas
// ============================================================================

export const profileIdParamSchema = z.object({
  id: z.string().min(1, 'Profile ID is required'),
});

// ============================================================================
// Query Schemas
// ============================================================================

export const listProfilesQuerySchema = z.object({
  category: profileCategorySchema.optional(),
});

// ============================================================================
// Request Body Schemas
// ============================================================================

export const createProfileRequestSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .max(100, 'Name must be 100 characters or less'),
    description: z
      .string()
      .max(500, 'Description must be 500 characters or less')
      .optional(),
    baseCategory: profileCategorySchema,
    binding: profileBindingSchema.optional(),
    toggles: promptTogglesSchema,
  })
  .strict();

// binding is mutable: null unbinds, a value rebinds (UNIQUE conflict → 409).
// Lets users attach a duplicated/custom profile to a category without
// recreating it from scratch.
export const updateProfileRequestSchema = createProfileRequestSchema
  .omit({ binding: true })
  .partial()
  .extend({
    binding: profileBindingSchema.nullable().optional(),
  });

export const duplicateProfileRequestSchema = z
  .object({
    name: z.string().min(1).max(100),
  })
  .strict();

/**
 * Restore the package default profile for a binding — the way back after
 * delete-as-opt-out. 409 if a profile with this binding already exists.
 */
export const restoreDefaultProfileRequestSchema = z
  .object({
    binding: profileBindingSchema,
  })
  .strict();

/**
 * Preview analyze request - test prompt profile against transcript
 *
 * Used in POST /api/v1/prompt-profiles/preview to validate profile configuration
 * before applying to production. Returns LLM output and generated prompts.
 */
export const previewAnalyzeRequestSchema = z
  .object({
    promptProfileId: z.string().optional(), // If omitted, uses default toggles
    /**
     * Проект, в области которого считать превью.
     *
     * Профили — организационные, и без этого поля превью тоже собиралось на
     * org-уровне. Но часть входов промпта рантайм берёт ИЗ ПРОЕКТА
     * (`analysisLanguage`, имя и описание проекта — `analyze/worker.ts`),
     * поэтому org-уровневое превью показывало промпт, которого в проде не
     * будет. Передан — превью считается «как будет в этом проекте»; опущен —
     * прежнее поведение (#321).
     */
    projectId: z.string().optional(),
    transcriptText: z.string().min(1).max(100000), // Full transcript or sample
    durationMinutes: z.number().min(0).optional(), // Call duration for context
    channelName: z.string().max(100).optional(), // Source channel (phone, chat, email)
    callDirection: z.enum(['inbound', 'outbound']).optional(),
    // Same set, same reason as `profileBindingSchema`: a preview can only be
    // run for a category the categorize-step actually emits.
    callCategory: analyzedCallCategorySchema.optional(),
    // Capped like transcriptText: each free-text field goes through PII
    // redaction on the edge-api request path before the LLM call (#1262).
    crmContext: z.string().max(20000).optional(), // Existing CRM data for enrichment
    /**
     * ПРИНИМАЕТСЯ, НО НЕ ДЕЙСТВУЕТ.
     *
     * Превью считается моделью платформы (`defaults.llmModel`), а не той, что
     * назвал запрос: выбор модели убран из интерфейса, чтобы продукт не
     * называл клиенту вендора и версию (#368). Поле оставлено, потому что
     * схема `.strict()`, и старый бандл SPA, который его ещё шлёт, иначе
     * получил бы 400 на онбординге превью.
     */
    model: z.string().optional(),
    toggles: z.record(z.string(), z.boolean()).optional(), // Ad-hoc toggle overrides
    // Additional context fields from PromptBuildContext
    acousticContext: z.string().max(5000).optional(), // Audio quality metrics (silence, overlap, clarity)
    companyContext: z.string().max(10000).optional(), // Organization-specific context
    outputLanguage: z.string().optional(), // Response language (default: en)
    createdAt: z.string().max(100).optional(), // Timestamp for analysis context
  })
  .strict();

// ============================================================================
// Response Schemas
// ============================================================================

export const promptProfileResponseSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  baseCategory: profileCategorySchema,
  category: profileCategorySchema, // Alias for baseCategory (backwards compatibility for UI)
  binding: profileBindingSchema.nullable(),
  toggles: z.record(z.string(), z.boolean()), // Parsed from JSON string by API
  systemPrompt: z.string().nullable(),
  userTemplate: z.string().nullable(),
  version: z.number().int().default(1),
  isActive: z.boolean().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByUserId: z.string().nullable(),
});

export const listProfilesResponseSchema = z.object({
  profiles: z.array(promptProfileResponseSchema),
  total: z.number().int(),
});

export const getProfileResponseSchema = z.object({
  profile: promptProfileResponseSchema,
});

export const createProfileResponseSchema = z.object({
  profile: promptProfileResponseSchema,
  message: z.string().optional(),
});

export const updateProfileResponseSchema = z.object({
  profile: promptProfileResponseSchema,
  message: z.string().optional(),
});

export const deleteProfileResponseSchema = z.object({
  message: z.string(),
});

/**
 * Preview analyze response - returns LLM analysis and generated prompts
 *
 * Allows debugging and validation of prompt profile configuration before deployment.
 */
export const previewAnalyzeResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    structuredOutput: z.record(z.string(), z.unknown()), // LLM JSON output (qualityScore, summary, etc.)
    prompts: z.object({
      systemPrompt: z.string(), // Generated system prompt with enabled toggles
      userPrompt: z.string(), // Generated user prompt with transcript and context
    }),
  }),
  metadata: z.record(z.string(), z.unknown()).optional(), // Token usage, model, execution time
});

// ============================================================================
// Type exports
// ============================================================================

export type ProfileCategory = z.infer<typeof profileCategorySchema>;
export type ProfileBinding = z.infer<typeof profileBindingSchema>;
export type PromptToggles = z.infer<typeof promptTogglesSchema>;
export type CreateProfileRequest = z.infer<typeof createProfileRequestSchema>;
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;
export type DuplicateProfileRequest = z.infer<typeof duplicateProfileRequestSchema>;
export type RestoreDefaultProfileRequest = z.infer<
  typeof restoreDefaultProfileRequestSchema
>;
export type PreviewAnalyzeRequest = z.infer<typeof previewAnalyzeRequestSchema>;
export type PromptProfileResponse = z.infer<typeof promptProfileResponseSchema>;
export type PromptProfile = PromptProfileResponse; // Alias for UI compatibility
export type PromptProfilesListResponse = z.infer<typeof listProfilesResponseSchema>;
